import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { CaseContactMethodViewModel, PolicyHolderInfo } from "interfaces/Mueller.Business.Models";
import { InitialState } from "reducers/initialState";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { contactTypeCodes } from "typeConstants/contactEfforts";

interface AppointmentConfirmationProps {
  onSave: (contacts: string[]) => void;
  onCancel: () => void;
  saveButtonLabel: string;
  // Redux
  policyHolderInfo: PolicyHolderInfo;
  contactMethods: CaseContactMethodViewModel[];
}

interface AppointmentConfirmationForm {
  sendConfirmation: boolean;
  sendToDesignated1: boolean;
  sendToDesignated2: boolean;
  sendToDesignatedEmail: boolean;
  sendToHomePhone: boolean;
  sendToWorkPhone: boolean;
  sendToEmail: boolean;
  sendToOther: boolean;
  otherContact: string;
}

const AppointmentConfirmation = (props: AppointmentConfirmationProps) => {
  const [form, setForm] = useState<AppointmentConfirmationForm>({
    sendConfirmation: true,
    sendToDesignated1: false,
    sendToDesignated2: false,
    sendToDesignatedEmail: false,
    sendToHomePhone: false,
    sendToWorkPhone: false,
    sendToEmail: false,
    sendToOther: false,
    otherContact: ""
  });

  const [designated1, setDesignated1] = useState<string>(null);
  const [designated2, setDesignated2] = useState<string>(null);
  const [designatedEmail, setDesignatedEmail] = useState<string>(null);

  useEffect(() => {
    if (props.contactMethods) {
      for(let contact of props.contactMethods) {
        if (contact.contactTypeCode === contactTypeCodes.D1) {
          setDesignated1(contact.contactValue);
        } else if (contact.contactTypeCode === contactTypeCodes.D2) {
          setDesignated2(contact.contactValue);
        } else if (contact.contactTypeCode === contactTypeCodes.DE) {
          setDesignatedEmail(contact.contactValue);
        }
      }
    }
  }, []);

  const onSendConfirmationChange = (value: boolean) => setForm({...form, sendConfirmation: value});
  const onSendToDesignated1Change = (value: boolean) => setForm({...form, sendToDesignated1: value});
  const onSendToDesignated2Change = (value: boolean) => setForm({...form, sendToDesignated2: value});
  const onSendToDesignatedEmailChange = (value: boolean) => setForm({...form, sendToDesignatedEmail: value});
  const onSendToHomePhoneChange = (value: boolean) => setForm({...form, sendToHomePhone: value});
  const onSendToWorkPhoneChange = (value: boolean) => setForm({...form, sendToWorkPhone: value});
  const onSendToEmailChange = (value: boolean) => setForm({...form, sendToEmail: value});
  const onSendToOtherChange = (value: boolean) => setForm({...form, sendToOther: value});
  const onOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => setForm({...form, otherContact: e.target.value});

  const handleSave = () => {
    const contacts: string[] = [];
    if (form.sendConfirmation) {
      if (form.sendToDesignated1 && designated1) contacts.push(designated1);
      if (form.sendToDesignated2 && designated2) contacts.push(designated2);
      if (form.sendToDesignatedEmail && designatedEmail) contacts.push(designatedEmail);
      if (form.sendToHomePhone) contacts.push(props.policyHolderInfo?.policyholderPhoneHome);
      if (form.sendToWorkPhone) contacts.push(props.policyHolderInfo?.policyholderPhoneWork);
      if (form.sendToEmail) contacts.push(props.policyHolderInfo?.policyholderEmail);
      if (form.sendToOther) contacts.push(form.otherContact);
    }
    props.onSave(contacts);
  };

  // TODO: Validate phone/email in other field?
  const isValid = !form.sendConfirmation 
    || (form.sendConfirmation && (form.sendToHomePhone || form.sendToWorkPhone || form.sendToEmail
      || form.sendToDesignated1 || form.sendToDesignated2 || form.sendToDesignatedEmail
      || (form.sendToOther && form.otherContact.length > 3)));

  return (
    <div>
      <FormControlLabel
        control={
          <Checkbox
            id="assign-appointment-dialog-send-confirmation"
            checked={form.sendConfirmation}
            onChange={(e) => onSendConfirmationChange(e.target.checked)}
            name="Send Confirmation"
          />
        }
        label="Send Appointment Confirmation"
      />
      {form.sendConfirmation &&
        <div className="assign-appointment-contact-options-container">
          {designated1 &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-designated1"
                  checked={form.sendToDesignated1}
                  onChange={(e) => onSendToDesignated1Change(e.target.checked)}
                  name="Designated 1"
                />
              }
              label={designated1}
            />
          }
          {designated2 &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-designated2"
                  checked={form.sendToDesignated2}
                  onChange={(e) => onSendToDesignated2Change(e.target.checked)}
                  name="Designated 2"
                />
              }
              label={designated2}
            />
          }
          {designatedEmail &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-designated-email"
                  checked={form.sendToDesignatedEmail}
                  onChange={(e) => onSendToDesignatedEmailChange(e.target.checked)}
                  name="Designated Email"
                />
              }
              label={designatedEmail}
            />
          }
          {props.policyHolderInfo?.policyholderPhoneHome &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-home-phone"
                  checked={form.sendToHomePhone}
                  onChange={(e) => onSendToHomePhoneChange(e.target.checked)}
                  name="Home Phone"
                />
              }
              label={props.policyHolderInfo?.policyholderPhoneHome}
            />
          }
          {props.policyHolderInfo?.policyholderPhoneWork &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-work-phone"
                  checked={form.sendToWorkPhone}
                  onChange={(e) => onSendToWorkPhoneChange(e.target.checked)}
                  name="Work Phone"
                />
              }
              label={props.policyHolderInfo?.policyholderPhoneWork}
            />
          }
          {props.policyHolderInfo?.policyholderEmail &&
            <FormControlLabel
              control={
                <Checkbox
                  id="assign-appointment-send-email"
                  checked={form.sendToEmail}
                  onChange={(e) => onSendToEmailChange(e.target.checked)}
                  name="Email"
                />
              }
              label={props.policyHolderInfo?.policyholderEmail}
            />
          }
          <FormControlLabel
            control={
              <Checkbox
                id="assign-appointment-send-other"
                checked={form.sendToOther}
                onChange={(e) => onSendToOtherChange(e.target.checked)}
                name="Other"
              />
            }
            label="Send to other email or phone number"
          />
          {form.sendToOther && 
            <TextField
              id="assign-appointment-send-other-field"
              inputProps={{ maxLength: 500 }}
              value={form.otherContact}
              onChange={onOtherChange}
              label="Enter an email address or phone number:"
              variant="standard"
            />
          }
        </div>
      }
      {!form.sendConfirmation && <p className="assign-appointment-no-confirm-warning">No Appointment Confirmation Will Be Sent</p>}
      <div className="flex-row-between">
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button 
          onClick={handleSave}
          disabled={!isValid}
        >{props.saveButtonLabel}</Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  policyHolderInfo: state.caseForms.caseHeaderV2?.caseHeaderDto?.policyInfo?.policyHolderInfo,
  contactMethods: state.caseForms.caseHeaderV2?.caseHeaderDto?.policyInfo?.caseContactMethods
});

export default connect(mapStateToProps, null)(AppointmentConfirmation);